/* import __COLOCATED_TEMPLATE__ from './overview.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Invokable } from '@glint/template/-private/integration';
import { type ViewId } from 'embercom/components/knowledge-hub/setup-modal';
import { EntityType } from 'embercom/models/data/entity-types';
import { type KnowledgeUsageSummary, type SyncSourceType } from './overview/types';
import Conversations from './overview/sections/conversations';
import InternalArticles from './overview/sections/internal-articles';
import MoreSources from './overview/sections/more-sources';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import PublicArticles from './overview/sections/public-articles';
import Websites from './overview/sections/websites';
import type Store from '@ember-data/store';
import Macros from './overview/sections/macros';
import ConversationExtraction from './overview/sections/conversation-extraction';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';

export interface OverviewArgs {
  activeTab?: ContentFilter | undefined;
  showManageConversationsDrawer?: boolean;
  showManageMacrosModal?: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: OverviewArgs;
}

export enum ContentFilter {
  All = 'all',
  AiAgent = 'ai-agent',
  AiCopilot = 'ai-copilot',
  HelpCenter = 'help-center',
}

export const VALID_CONTENT_FILTERS = {
  [ContentFilter.All]: true,
  [ContentFilter.AiAgent]: true,
  [ContentFilter.AiCopilot]: true,
  [ContentFilter.HelpCenter]: true,
};

interface SetupModalContext {
  showImportSourcesTiles?: boolean;
  showAddContentTiles?: boolean;
  isStandaloneUnifiedContentSeeAll?: boolean;
  isConversationHistorySynced?: boolean;
}

const SOURCE_TYPE_TO_ENTITY_TYPE: Record<SyncSourceType | 'file-upload', EntityType> = {
  guru: EntityType.InternalArticle,
  confluence: EntityType.InternalArticle,
  notion: EntityType.InternalArticle,
  zendesk: EntityType.ArticleContent,
  external_content: EntityType.ExternalContent,
  zendesk_tickets: EntityType.Conversation,
  'file-upload': EntityType.FileSourceContent,
  salesforce_knowledge: EntityType.FileSourceContent,
  salesforce_cases: EntityType.Conversation,
  box: EntityType.FileSourceContent,
  document360: EntityType.FileSourceContent,
  freshdesk: EntityType.FileSourceContent,
  freshdesk_tickets: EntityType.Conversation,
  shopify: EntityType.EcommerceProduct,
  hubspot: EntityType.ArticleContent,
};

export interface OverviewSectionArgs {
  knowledgeUsageSummary: KnowledgeUsageSummary;
  contentFilter: ContentFilter;
  onAddSource: (sourceType: SyncSourceType | 'file-upload') => void;
  showManageConversationsDrawer?: boolean;
  showManageMacrosModal?: boolean;
}

export default class KnowledgeHubOverview extends Component<Signature> {
  @service declare appService: AppService;
  @service declare router: any;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare conversationExtractionService: ConversationExtractionService;
  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare intercomEventService: { trackAnalyticsEvent: Function };
  @service declare helpCenterService: $TSFixMe;
  @service declare store: Store;
  @service declare modalService: $TSFixMe;

  @tracked showSetupModal = false;
  @tracked setupModalEntryPoint?: ViewId = undefined;
  @tracked setupModalContext: SetupModalContext = {};
  @tracked selectedEntityType: EntityType | null = null;
  @tracked contentFilter: ContentFilter = ContentFilter.All;
  @tracked sectionsConfigs: {
    component: Invokable<any>;
    visibleFor: ContentFilter[];
  }[];
  @tracked showCreateCollectionModal = false;
  @tracked openSectionId?: string;
  @tracked showPreview = true;
  get shouldShowHeader(): boolean {
    return !(this.appService.app.canUseStandalone && this.appService.app.canUseUnifiedContentUI);
  }

  get canUseUnifiedContentUI(): boolean {
    return this.appService.app.canUseUnifiedContentUI;
  }

  public constructor(owner: any, args: any) {
    super(owner, args);
    if (this.args.activeTab && VALID_CONTENT_FILTERS[this.args.activeTab]) {
      this.contentFilter = this.args.activeTab;
    }
    if (this.appService.app.canUseStandalone) {
      this.contentFilter = ContentFilter.AiAgent;
    }
    this.knowledgeHubService.subscribeToSourceSyncEvents();
    this.sectionsConfigs = [
      {
        component: PublicArticles,
        visibleFor: [ContentFilter.All, ContentFilter.AiAgent, ContentFilter.HelpCenter],
      },
      {
        component: InternalArticles,
        visibleFor: [ContentFilter.All, ContentFilter.AiCopilot],
      },
      {
        component: Conversations,
        visibleFor: [ContentFilter.All, ContentFilter.AiCopilot],
      },
      {
        component: Macros,
        visibleFor: [ContentFilter.All, ContentFilter.AiCopilot],
      },
      {
        component: PublicArticles,
        visibleFor: [ContentFilter.AiCopilot],
      },
      {
        component: Websites,
        visibleFor: [ContentFilter.All, ContentFilter.AiCopilot, ContentFilter.AiAgent],
      },
      {
        component: MoreSources,
        visibleFor: [ContentFilter.All, ContentFilter.AiCopilot, ContentFilter.AiAgent],
      },
    ];

    // Standalone workspaces don't use Intercom Macros so we want to filter them out here.
    if (this.appService.app.canUseStandalone) {
      this.sectionsConfigs = this.sectionsConfigs.filter((section) => section.component !== Macros);
    }

    if (this.shouldUseConversationExtraction) {
      this.sectionsConfigs.push({
        component: ConversationExtraction,
        visibleFor: [ContentFilter.All, ContentFilter.AiAgent, ContentFilter.AiCopilot],
      });
      this.conversationExtractionService.subscribeToImportProgressEvents();
    }
  }

  willDestroy() {
    super.willDestroy();
    this.knowledgeHubService.unsubscribeToSourceSyncEvents();
    if (this.shouldUseConversationExtraction) {
      this.conversationExtractionService.unsubscribeFromImportProgressEvents();
    }
  }

  get shouldUseConversationExtraction() {
    return false;
  }

  get usageSummary(): KnowledgeUsageSummary {
    return this.knowledgeHubService.usageSummary || {};
  }

  @action setOpenSectionId(id: string | undefined) {
    this.openSectionId = id;
  }

  @action togglePreview() {
    this.showPreview = !this.showPreview;
  }

  @action openSetupModalForEntity(entityType: EntityType) {
    this.showSetupModal = true;
    this.selectedEntityType = entityType;
  }

  @action changeTab(filter: ContentFilter) {
    this.contentFilter = filter;
    this.scrollToTop();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'tab',
      section: 'knowledge_hub',
      place: 'sources',
      tab: filter,
    });
  }

  private scrollToTop() {
    document.querySelector('.overview__main-container')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @action async closeSetupModal() {
    this.showSetupModal = false;
    this.selectedEntityType = null;
    this.setupModalEntryPoint = undefined;
    this.setupModalContext = {};
    await this.knowledgeHubService.removeEmptySources();
    this.knowledgeHubService.onSummaryChange();
  }

  @action
  recordAnalyticsEvent(analyticsId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: analyticsId,
      place: 'knowledge-hub-overview-header',
    });
  }

  get sectionComponents() {
    return this.sectionsConfigs.filter((section) =>
      section.visibleFor.includes(this.contentFilter),
    );
  }

  @action async showModal(syncSourceType: SyncSourceType | 'file-upload' | undefined) {
    // Handle "See all" case - when syncSourceType is undefined, open the setup modal
    if (syncSourceType === undefined) {
      // For standalone apps, show "Connect external app content" modal
      if (this.appService.app.canUseStandalone) {
        this.setupModalEntryPoint = 'select-content';
        this.setupModalContext = {
          showImportSourcesTiles: true,
          showAddContentTiles: false,
          isStandaloneUnifiedContentSeeAll: true,
          isConversationHistorySynced:
            this.appService.mainStandalonePlatform?.historicalSyncingEnabled,
        };
      } else {
        this.setupModalEntryPoint = undefined;
        this.setupModalContext = {};
      }
      this.showSetupModal = true;
      return;
    }

    try {
      let entityType = this.entityTypeForSourceType(syncSourceType);
      await this.knowledgeHubService.ensurePermissions(entityType);
    } catch (error) {
      return;
    }

    if (syncSourceType === 'zendesk') {
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'zendesk',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    // Check if we should use the React drawer for Confluence
    if (syncSourceType === 'confluence' && this.appService.app.canUseConfluenceReactImporter) {
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'confluence',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    // Check if we should use the React drawer for Notion
    if (syncSourceType === 'notion' && this.appService.app.canUseNotionReactImporter) {
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'notion',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    // Check if we should use the React drawer for Guru
    if (syncSourceType === 'guru' && this.appService.app.canUseGuruReactImporter) {
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'guru',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    // HubSpot always uses the React drawer (no legacy modal)
    if (
      syncSourceType === 'hubspot' &&
      this.appService.app.canUseFeature('team-knowledge-foundations-hubspot-react-importer')
    ) {
      this.modalService.openModal(
        'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
        {
          open: true,
          appId: this.appService.app.id,
          adminId: this.appService.app.currentAdmin.id.toString(),
          initialSourceId: 'hubspot',
          onClose: () => this.modalService.closeModal(),
        },
      );
      return;
    }

    switch (syncSourceType) {
      case 'guru':
        this.setupModalEntryPoint = 'guru-sync';
        break;
      case 'confluence':
        this.setupModalEntryPoint = 'confluence-sync';
        break;
      case 'notion':
        this.setupModalEntryPoint = 'notion-sync';
        break;
      case 'external_content':
        this.setupModalEntryPoint = 'external-content';
        break;
      case 'file-upload':
        this.setupModalEntryPoint = 'file-upload';
        break;
      case 'salesforce_knowledge':
        this.setupModalEntryPoint = 'salesforce-sync';
        break;
      case 'box':
        this.setupModalEntryPoint = 'box-sync';
        break;
      case 'document360':
        this.setupModalEntryPoint = 'document360-sync';
        break;
      case 'freshdesk':
        this.setupModalEntryPoint = 'freshdesk-sync';
        break;
      case 'shopify':
        this.setupModalEntryPoint = 'shopify-sync';
        break;
      default:
        return;
    }
    this.showSetupModal = true;
  }

  private entityTypeForSourceType(syncSourceType: SyncSourceType | 'file-upload'): EntityType {
    return SOURCE_TYPE_TO_ENTITY_TYPE[syncSourceType];
  }

  get contentFilterIsValidForChecklist(): boolean {
    return this.contentFilter !== ContentFilter.All;
  }

  @action updateOverviewChecklistAttributes() {
    this.knowledgeHubService.getOverviewChecklistAttributes();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Overview': typeof KnowledgeHubOverview;
  }
}
